import { containerCapacity, getItemDef, usedCells, type ContainerItemDef, type GridState, type Inventory, type ItemInstance } from "@rietz/shared";
import type { OpenContainer } from "../store.js";
import { boxAt } from "./drag.js";

/**
 * "Which box is open, and what is in it" — the store holds an IID, and this
 * turns it back into something a panel can draw, called fresh on EVERY render:
 * a box that MOVES keeps its panel, one that VANISHES stops resolving (the
 * screen clears the id), and the fill readout comes from the same grid the
 * tiles do. No DOM, no store reads.
 */

/** An open box, resolved: everything a panel needs and nothing more. */
export interface OpenBox {
  def: ContainerItemDef;
  item: ItemInstance;
  /** The contents. Empty (and freshly minted) when the box holds nothing —
   *  absent contents ARE the empty grid, see `contentsGridFor`. */
  grid: GridState;
  /** Occupied cells over the def's capacity, for the panel header. */
  fill: { used: number; total: number };
  /** The address the drag layer must use for this displayed capacity. Worn
   * bags have hoisted top-level grids; stowed containers have nested grids. */
  target:
    | { area: "rig" | "backpack"; boxIid?: undefined }
    | { area: "inside"; boxIid: string };
}

/**
 * The box `boxIid` names, or `null` — nothing open, unreachable, or not a
 * container. Searched over the DISPLAYED state, nested grids included, to
 * `MAX_GRID_DEPTH`.
 */
export function resolveOpenBox(
  inv: Inventory,
  container: OpenContainer | null,
  boxIid: string | null,
): OpenBox | null {
  if (boxIid === null) return null;

  // Equipped bags do not carry `contents`: equip hoists that grid onto the
  // inventory. Resolve those slots before descending through stowed items.
  for (const area of ["rig", "backpack"] as const) {
    const item = inv.equipment[area];
    const grid = inv[area];
    if (item === null || grid === null || item.iid !== boxIid) continue;
    const def = getItemDef(item.defId);
    if (def === null || def.kind !== "container") return null;
    return {
      def,
      item,
      grid,
      fill: { used: usedCells(grid), total: containerCapacity(def) },
      target: { area },
    };
  }

  const box = boxAt(inv, container, boxIid);
  if (box === null) return null;
  return {
    def: box.def,
    item: box.item,
    grid: box.grid,
    // Capacity from the DEF, not the grid (a disagreeing grid is corrupt), and
    // the
    // POUCHES when there are any — the cells between them can never hold
    // anything.
    fill: { used: usedCells(box.grid), total: containerCapacity(box.def) },
    target: { area: "inside", boxIid: box.item.iid },
  };
}
